import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, mediaUrl } from "@/lib/api";
import { useI18n } from "@/i18n";
import { ICONS, ICON_KEYS } from "@/lib/amenityIcons";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Globe, Sparkles, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LANG_TABS = [{ c: "fr", l: "FR" }, { c: "th", l: "ไทย" }, { c: "en", l: "EN" }];
const blank3 = () => ({ fr: "", th: "", en: "" });

function ImagePicker({ path, onChange, onUploading, testid, aspect = "aspect-video" }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); onUploading?.(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/site-content/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setBusy(false); onUploading?.(false);
    e.target.value = "";
  };
  return (
    <div className="space-y-2">
      <div className={`relative ${aspect} w-full rounded-md overflow-hidden border border-border bg-sand/40 flex items-center justify-center`}>
        {path ? <img src={mediaUrl(path)} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
              : <ImagePlus className="w-8 h-8 text-muted-foreground" />}
        {busy && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teak" /></div>}
        {path && !busy && (
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow">
            <X className="w-4 h-4 text-destructive" />
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={pick} data-testid={`${testid}-input`} />
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testid}-btn`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ImagePlus className="w-4 h-4 mr-1" />}
        {path ? t("cms.replace") : t("cms.addImage")}
      </Button>
    </div>
  );
}

const Section = ({ title, children }) => (
  <Card className="p-6 border-border space-y-4">
    <h2 className="text-lg font-semibold text-teak">{title}</h2>
    {children}
  </Card>
);

export default function SiteContent() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["public-content"], queryFn: () => api.get("/public/content").then((r) => r.data) });
  const { data: rooms = [] } = useQuery({ queryKey: ["public-rooms"], queryFn: () => api.get("/public/rooms").then((r) => r.data) });
  const [c, setC] = useState(null);
  const [clang, setClang] = useState("fr");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data && !c) {
      const d = JSON.parse(JSON.stringify(data));
      d.hero_image = d.hero_image || "";
      d.gallery = d.gallery || [];
      d.room_images = d.room_images || {};
      setC(d);
    }
  }, [data]); // eslint-disable-line

  if (!c) return null;

  const setPath = (section, field, val) => setC({ ...c, [section]: { ...c[section], [field]: { ...c[section][field], [clang]: val } } });
  const setContactPlain = (field, val) => setC({ ...c, contact: { ...c.contact, [field]: val } });

  const addAmenity = () => setC({ ...c, amenities: [...c.amenities, { id: `a${Date.now()}`, icon: "sparkles", title: blank3(), desc: blank3() }] });
  const removeAmenity = (id) => setC({ ...c, amenities: c.amenities.filter((a) => a.id !== id) });
  const setAmenity = (id, key, val) => setC({ ...c, amenities: c.amenities.map((a) => a.id === id ? { ...a, [key]: val } : a) });
  const setAmenityText = (id, key, val) => setC({ ...c, amenities: c.amenities.map((a) => a.id === id ? { ...a, [key]: { ...a[key], [clang]: val } } : a) });
  const setAmenityLangText = (id, key, lc, val) => setC({ ...c, amenities: c.amenities.map((a) => a.id === id ? { ...a, [key]: { ...(a[key] && typeof a[key] === "object" ? a[key] : {}), [lc]: val } } : a) });

  const addAttraction = () => setC({ ...c, attractions: [...(c.attractions || []), { id: `t${Date.now()}`, image: "", title: blank3(), desc: blank3() }] });
  const removeAttraction = (id) => setC({ ...c, attractions: c.attractions.filter((a) => a.id !== id) });
  const setAttraction = (id, key, val) => setC({ ...c, attractions: c.attractions.map((a) => a.id === id ? { ...a, [key]: val } : a) });
  const setAttractionText = (id, key, val) => setC({ ...c, attractions: c.attractions.map((a) => a.id === id ? { ...a, [key]: { ...a[key], [clang]: val } } : a) });
  const setAttractionLangText = (id, key, lc, val) => setC({ ...c, attractions: c.attractions.map((a) => a.id === id ? { ...a, [key]: { ...(a[key] && typeof a[key] === "object" ? a[key] : {}), [lc]: val } } : a) });

  const addGallery = (p) => setC({ ...c, gallery: [...c.gallery, p] });
  const removeGallery = (i) => setC({ ...c, gallery: c.gallery.filter((_, idx) => idx !== i) });
  const setRoomImage = (num, p) => setC({ ...c, room_images: { ...c.room_images, [num]: p } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site-content", c);
      qc.invalidateQueries({ queryKey: ["public-content"] });
      toast.success(t("cms.saved"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak flex items-center gap-2"><Globe className="w-7 h-7" />{t("cms.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("cms.subtitle")}</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-teak hover:bg-teak-hover text-white" data-testid="cms-save-btn"><Save className="w-4 h-4 mr-1" />{t("common.save")}</Button>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">{t("cms.lang")} :</span>
        <div className="flex gap-1 bg-white rounded-full border border-border p-1">
          {LANG_TABS.map((l) => (
            <button key={l.c} onClick={() => setClang(l.c)} data-testid={`cms-lang-${l.c}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${clang === l.c ? "bg-teak text-white" : "text-muted-foreground"}`}>{l.l}</button>
          ))}
        </div>
      </div>

      <Section title={t("cms.presentation")}>
        <div className="space-y-2"><Label>{t("cms.heroImage")}</Label>
          <p className="text-xs text-muted-foreground">{t("cms.imgHint")}</p>
          <ImagePicker path={c.hero_image} onChange={(p) => setC({ ...c, hero_image: p })} testid="cms-hero-image" />
        </div>
        <div className="space-y-2"><Label>{t("cms.heroTitle")}</Label><Input value={c.hero.title[clang] || ""} onChange={(e) => setPath("hero", "title", e.target.value)} data-testid="cms-hero-title" /></div>
        <div className="space-y-2"><Label>{t("cms.tagline")}</Label><Input value={c.hero.tagline[clang] || ""} onChange={(e) => setPath("hero", "tagline", e.target.value)} data-testid="cms-hero-tagline" /></div>
        <div className="space-y-2"><Label>{t("cms.intro")}</Label><Textarea rows={3} value={c.hero.intro[clang] || ""} onChange={(e) => setPath("hero", "intro", e.target.value)} data-testid="cms-hero-intro" /></div>
      </Section>

      <Section title={t("cms.amenities")}>
        <div className="space-y-3">
          {c.amenities.map((a) => {
            const Ico = ICONS[a.icon] || Sparkles;
            return (
              <div key={a.id} className="rounded-md border border-border p-3 flex flex-col sm:flex-row gap-3" data-testid={`cms-amenity-${a.id}`}>
                <div className="sm:w-40 shrink-0 space-y-1">
                  <Label className="text-xs">{t("cms.icon")}</Label>
                  <Select value={a.icon} onValueChange={(v) => setAmenity(a.id, "icon", v)}>
                    <SelectTrigger data-testid={`cms-amenity-icon-${a.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {ICON_KEYS.map((k) => { const I = ICONS[k]; return <SelectItem key={k} value={k}><span className="flex items-center gap-2"><I className="w-4 h-4" />{k}</span></SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-2">
                  {LANG_TABS.map((l) => (
                    <div key={l.c} className="flex items-center gap-2">
                      <span className="w-9 shrink-0 text-[11px] font-semibold uppercase text-muted-foreground">{l.l}</span>
                      <div className="flex-1 space-y-1">
                        <Input value={(typeof a.title === "object" ? a.title[l.c] : "") || ""} onChange={(e) => setAmenityLangText(a.id, "title", l.c, e.target.value)} placeholder={t("cms.itemTitle")} data-testid={`cms-amenity-title-${a.id}-${l.c}`} />
                        <Input value={(typeof a.desc === "object" ? a.desc[l.c] : "") || ""} onChange={(e) => setAmenityLangText(a.id, "desc", l.c, e.target.value)} placeholder={t("cms.itemDesc")} data-testid={`cms-amenity-desc-${a.id}-${l.c}`} />
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeAmenity(a.id)} className="self-start" data-testid={`cms-amenity-del-${a.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            );
          })}
        </div>
        <Button variant="outline" size="sm" onClick={addAmenity} data-testid="cms-add-amenity"><Plus className="w-4 h-4 mr-1" />{t("cms.addAmenity")}</Button>
      </Section>

      <Section title={t("cms.attractions")}>
        <p className="text-xs text-muted-foreground">{t("cms.imgHint")}</p>
        <p className="text-xs text-terracotta">{t("cms.langHint")}</p>
        <div className="space-y-3">
          {(c.attractions || []).map((a) => (
            <div key={a.id} className="rounded-md border border-border p-3 flex flex-col sm:flex-row gap-3" data-testid={`cms-attraction-${a.id}`}>
              <div className="sm:w-40 shrink-0"><ImagePicker path={a.image} onChange={(p) => setAttraction(a.id, "image", p)} testid={`cms-attraction-img-${a.id}`} aspect="aspect-[4/3]" /></div>
              <div className="flex-1 space-y-2">
                {LANG_TABS.map((l) => (
                  <div key={l.c} className="flex items-start gap-2">
                    <span className="w-9 shrink-0 pt-2 text-[11px] font-semibold uppercase text-muted-foreground">{l.l}</span>
                    <div className="flex-1 space-y-1">
                      <Input value={(typeof a.title === "object" ? a.title[l.c] : "") || ""} onChange={(e) => setAttractionLangText(a.id, "title", l.c, e.target.value)} placeholder={t("cms.itemTitle")} data-testid={`cms-attraction-title-${a.id}-${l.c}`} />
                      <Textarea rows={2} value={(typeof a.desc === "object" ? a.desc[l.c] : "") || ""} onChange={(e) => setAttractionLangText(a.id, "desc", l.c, e.target.value)} placeholder={t("cms.itemDesc")} data-testid={`cms-attraction-desc-${a.id}-${l.c}`} />
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="icon" onClick={() => removeAttraction(a.id)} className="self-start" data-testid={`cms-attraction-del-${a.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={addAttraction} data-testid="cms-add-attraction"><Plus className="w-4 h-4 mr-1" />{t("cms.addAttraction")}</Button>
      </Section>

      <Section title={t("cms.gallery")}>
        <p className="text-xs text-muted-foreground">{t("cms.imgHint")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {c.gallery.map((g, i) => (
            <div key={i} className="relative aspect-[4/3] rounded-md overflow-hidden border border-border" data-testid={`cms-gallery-${i}`}>
              <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeGallery(i)} data-testid={`cms-gallery-remove-${i}`}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"><X className="w-4 h-4 text-destructive" /></button>
            </div>
          ))}
        </div>
        <div className="max-w-[200px]"><ImagePicker path="" onChange={addGallery} testid="cms-gallery-add" aspect="aspect-[4/3]" /></div>
      </Section>

      <Section title={t("cms.roomPhotos")}>
        <p className="text-xs text-muted-foreground">{t("cms.imgHint")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {rooms.map((r) => (
            <div key={r.number} className="space-y-1" data-testid={`cms-room-${r.number}`}>
              <Label className="text-xs font-medium">{r.number}</Label>
              <ImagePicker path={c.room_images[r.number] || ""} onChange={(p) => setRoomImage(r.number, p)} testid={`cms-room-img-${r.number}`} aspect="aspect-[4/3]" />
            </div>
          ))}
        </div>
      </Section>

      <Section title={t("cms.location")}>
        <div className="space-y-2"><Label>{t("cms.itemTitle")}</Label><Input value={c.location.title[clang] || ""} onChange={(e) => setPath("location", "title", e.target.value)} data-testid="cms-loc-title" /></div>
        <div className="space-y-2"><Label>{t("cms.itemDesc")}</Label><Textarea rows={2} value={c.location.text[clang] || ""} onChange={(e) => setPath("location", "text", e.target.value)} data-testid="cms-loc-text" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-2"><Label>{t("cms.mapLat")}</Label><Input value={c.location.lat || ""} onChange={(e) => setC({ ...c, location: { ...c.location, lat: e.target.value } })} data-testid="cms-loc-lat" /></div>
          <div className="space-y-2"><Label>{t("cms.mapLng")}</Label><Input value={c.location.lng || ""} onChange={(e) => setC({ ...c, location: { ...c.location, lng: e.target.value } })} data-testid="cms-loc-lng" /></div>
        </div>
        <p className="text-xs text-muted-foreground">{t("cms.mapHint")}</p>
      </Section>

      <Section title={t("cms.contact")}>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-2"><Label>{t("cms.phone")}</Label><Input value={c.contact.phone || ""} onChange={(e) => setContactPlain("phone", e.target.value)} data-testid="cms-phone" /></div>
          <div className="space-y-2"><Label>{t("cms.email")}</Label><Input value={c.contact.email || ""} onChange={(e) => setContactPlain("email", e.target.value)} data-testid="cms-email" /></div>
        </div>
        <div className="space-y-2"><Label>{t("cms.notifyEmail")}</Label><Input value={c.contact.notify_email || ""} onChange={(e) => setContactPlain("notify_email", e.target.value)} data-testid="cms-notify-email" /><p className="text-xs text-muted-foreground">{t("cms.notifyEmailHint")}</p></div>
        <div className="space-y-2"><Label>{t("cms.address")}</Label><Input value={c.contact.address?.[clang] || ""} onChange={(e) => setPath("contact", "address", e.target.value)} data-testid="cms-address" /></div>
        <div className="space-y-2"><Label>{t("cms.note")}</Label><Textarea rows={2} value={c.contact.note?.[clang] || ""} onChange={(e) => setPath("contact", "note", e.target.value)} data-testid="cms-note" /></div>
      </Section>
    </div>
  );
}
